'use client';

import type { CaptureOptions } from 'posthog-js';
import { useCallback } from 'react';
import { logPostHogEvent } from '@upzi/web-shell/analytics';
import {
  enqueuePostHogOperation,
  getLoadedPostHog,
} from '@/lib/posthog-runtime';

export interface PostHogEventProperties {
  [key: string]: any;
}

export interface UserProperties {
  email?: string;
  name?: string;
  userId?: string;
  [key: string]: any;
}

/**
 * Custom PostHog hook that provides enhanced tracking capabilities
 * Keeps tracking calls stable while the PostHog client loads outside the
 * critical rendering path.
 */
export function usePosthog() {
  const posthog = getLoadedPostHog();

  /**
   * Track an event with optional properties
   */
  const track = useCallback(
    (
      eventName: string,
      properties?: PostHogEventProperties,
      options?: CaptureOptions
    ) => {
      logPostHogEvent(eventName, properties);
      enqueuePostHogOperation(client => {
        client.capture(eventName, properties, options);
      });
    },
    []
  );

  /**
   * Identify a user with properties
   */
  const identify = useCallback(
    (distinctId: string, properties?: UserProperties) => {
      enqueuePostHogOperation(client => {
        const isIdentified = client.get_distinct_id() === distinctId;

        if (!isIdentified) {
          client.identify(distinctId, properties);
        }
      });
    },
    []
  );

  /**
   * Set user properties without identifying
   */
  const setUserProperties = useCallback((properties: UserProperties) => {
    enqueuePostHogOperation(client => {
      client.setPersonProperties(properties);
    });
  }, []);

  /**
   * Reset the user (useful for logout)
   */
  const reset = useCallback(() => {
    enqueuePostHogOperation(client => {
      client.reset();
    });
  }, []);

  /**
   * Track page view manually
   */
  const trackPageView = useCallback((properties?: PostHogEventProperties) => {
    logPostHogEvent('$pageview', properties);
    enqueuePostHogOperation(client => {
      client.capture('$pageview', properties);
    });
  }, []);

  /**
   * Check if PostHog is available
   */
  const isAvailable = !!posthog;

  /**
   * Get the current distinct ID
   */
  const getDistinctId = useCallback(() => {
    if (!posthog) return null;

    return posthog.get_distinct_id();
  }, [posthog]);

  /**
   * Check if a feature flag is enabled
   */
  const isFeatureEnabled = useCallback(
    (flagKey: string) => {
      if (!posthog) return false;

      return posthog.isFeatureEnabled(flagKey);
    },
    [posthog]
  );

  /**
   * Get feature flag value
   */
  const getFeatureFlag = useCallback(
    (flagKey: string) => {
      if (!posthog) return undefined;

      return posthog.getFeatureFlag(flagKey);
    },
    [posthog]
  );

  return {
    // Original PostHog instance for advanced usage
    posthog,

    // Enhanced methods
    track,
    identify,
    setUserProperties,
    reset,
    trackPageView,
    isAvailable,
    getDistinctId,
    isFeatureEnabled,
    getFeatureFlag,
  };
}
